import React from 'react';
import { ShoppingCart, LogOut } from 'lucide-react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Badge, Dropdown } from 'react-bootstrap';
import AdminOrders from './AdminOrders';
import { isAdminLoggedIn, getAdminUser, adminLogout } from '../../services/AdminAuthService';
import logo from '../../Asset/Cakerestrologo.png';
import './AdminLayout.css';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        if (!isAdminLoggedIn()) {
            navigate('/admin-login');
        }
    }, [navigate]);

    const menuItems = [
        { icon: <ShoppingCart size={18} className="me-2 text-primary" />, label: 'Orders', path: '/admin/orders' },
    ];

    const handleLogout = () => {
        adminLogout();
        navigate('/admin-login');
    };

    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const adminUser = getAdminUser();

    if (!isAdminLoggedIn()) return null;

    return (
        <div className="admin-layout-wrapper">
            <header className="admin-header fixed-top bg-white border-bottom shadow-sm">
                <div className="admin-header-content px-lg-4 px-3 d-flex align-items-center h-100">
                    {/* Brand Section */}
                    <div className="d-flex align-items-center me-4">
                        <Link to="/admin" className="d-flex align-items-center gap-2 text-decoration-none">
                            <img src={logo} alt="CakeRestro" height="30" className="d-inline-block" />
                            <Badge bg="light" className="text-muted border x-small fw-bold letter-spacing-1 d-none d-xl-inline-block">ADMIN</Badge>
                        </Link>
                    </div>

                    {/* Desktop Navigation Tabs - One Row */}
                    <nav className="admin-desktop-nav d-none d-lg-flex h-100 align-items-center">
                        {menuItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`admin-nav-item px-3 d-flex align-items-center gap-2 transition-base text-decoration-none h-100 ${isActive ? 'active' : ''}`}
                                >
                                    <span className={`nav-icon d-flex align-items-center ${isActive ? 'text-primary' : 'text-muted'}`}>
                                        {React.cloneElement(item.icon, { size: 18 })}
                                    </span>
                                    <span className={`nav-label extra-small fw-bold text-uppercase letter-spacing-1 ${isActive ? 'text-primary' : 'text-muted'}`}>
                                        {item.label.split(' ')[0]}
                                    </span>
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Right Side Actions */}
                    <div className="ms-auto d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2 d-none d-sm-flex border-end pe-3 me-1">
                            <div className="text-end">
                                <div className="fw-bold text-dark extra-small text-uppercase mb-0" style={{ lineHeight: '1' }}>{adminUser?.name || 'Admin'}</div>
                                <div className="text-muted extra-small opacity-75 fw-bold" style={{ fontSize: '9px' }}>Super Admin</div>
                            </div>
                        </div>

                        {/* Direct Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="btn btn-link p-0 text-danger d-none d-md-flex align-items-center gap-2 text-decoration-none transition-base hover-scale"
                            title="Sign Out"
                        >
                            <div className="bg-soft-danger p-2 rounded-circle d-flex align-items-center justify-content-center">
                                <LogOut size={16} />
                            </div>
                            <span className="extra-small fw-bold text-uppercase letter-spacing-1">Logout</span>
                        </button>

                        <Dropdown align="end" className="admin-profile-dropdown d-md-none">
                            <Dropdown.Toggle as="div" className="no-caret cursor-pointer">
                                <div
                                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm border border-2 border-white"
                                    style={{ width: '36px', height: '36px', fontSize: '13px' }}
                                >
                                    {(adminUser?.name || 'A')[0]}
                                </div>
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="dropdown-menu-custom shadow-lg border-0 mt-2">
                                <div className="p-2">
                                    <Dropdown.Item onClick={handleLogout} className="rounded-3 py-2 d-flex align-items-center gap-3 text-danger text-decoration-none border-0">
                                        <LogOut size={16} />
                                        <span className="small fw-bold text-uppercase extra-small">Logout</span>
                                    </Dropdown.Item>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>

                        <button
                            className="bg-light rounded-circle border-0 p-2 d-lg-none text-dark d-flex align-items-center justify-content-center"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            style={{ width: '40px', height: '40px' }}
                        >
                            <div className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {mobileMenuOpen && (
                    <nav className="admin-mobile-nav d-lg-none bg-white border-top shadow-sm">
                        <div className="py-2 px-3">
                            {menuItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`d-flex align-items-center py-2 px-3 small text-decoration-none mb-1 rounded-3 ${location.pathname === item.path ? 'bg-primary bg-opacity-10 text-primary fw-bold' : 'text-muted'}`}
                                >
                                    <span className="me-3 d-flex align-items-center">{item.icon}</span>
                                    <span className="fw-bold extra-small text-uppercase letter-spacing-1">{item.label}</span>
                                </Link>
                            ))}
                        </div>
                    </nav>
                )}
            </header>

            {/* Main Content Area */}
            <main className="admin-main-horizontal mt-header pt-lg-5 pt-4 bg-light">
                <div className="admin-container pb-5">
                    <Routes>
                        <Route path="/" element={<AdminOrders />} />
                        <Route path="/orders" element={<AdminOrders />} />
                    </Routes>
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
